import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchGraphQL } from '@/lib/graphql/client';
import {
  GET_RESALE_ORDERS,
  GET_RESALE_ITEMS,
  GET_RESALE_ITEM,
  GET_RESALE_ANALYTICS,
  GET_RESALE_METRICS,
  GET_ENVIRONMENT_METRICS
} from './queries';
import {
  mockOrders,
  mockItems,
  mockItem,
  mockAnalytics,
  mockMetrics,
  mockEnvironmentMetrics
} from './mock-data';

// Mock data for development
const mockSettings = {
  programName: 'Tonic Resale',
  description: 'Our resale program for sustainable fashion',
  defaultPricingStrategy: 'fixed',
  minimumCondition: 'good',
  autoListItems: false,
  commissionRate: '10',
  minimumPrice: '10',
  maximumPrice: '1000',
  pricingStrategy: 'dynamic',
  acceptedPaymentMethods: ['credit_card', 'paypal'],
  requireInspectionPhotos: true,
  qualityGrades: ['A', 'B', 'C'],
  inspectionChecklist: '',
  photoRequirements: '',
  defaultShippingMethod: 'standard',
  shippingProviders: ['usps', 'fedex', 'ups'],
  packagingRequirements: '',
  returnShippingMethod: 'standard',
  lowStockThreshold: '5',
  autoRemoveAfterDays: '90',
  locationTracking: true,
  barcodeScanning: true,
  returnPeriod: '30',
  customerServiceEmail: '',
  automaticEmails: true,
  productDescriptionTemplate: '',
  carbonOffsetEnabled: true,
  recyclingPartners: '',
  sustainabilityMetrics: true,
  impactReporting: true,
  notificationEmails: '',
  alertThresholds: {
    lowStock: '5',
    highDemand: '20',
    priceChange: '10'
  },
  marketplaceIntegrations: ['ebay', 'poshmark'],
  apiEndpoints: '',
  webhookUrls: '',
  termsAndConditions: '',
  privacyPolicy: '',
  returnPolicy: '',
  prohibitedItems: '',
  analyticsEnabled: true,
  reportSchedule: 'weekly',
  customMetrics: '',
  rolePermissions: {
    admin: ['all'],
    manager: ['view', 'edit'],
    staff: ['view']
  },
  approvalWorkflow: true
};

// Resale Settings Hooks
export function useResaleSettings() {
  return useQuery({
    queryKey: ['resale', 'settings'],
    queryFn: async () => {
      // In development, return mock data
      if (process.env.NODE_ENV === 'development') {
        return mockSettings;
      }
      const response = await fetch('/api/resale/settings');
      if (!response.ok) throw new Error('Failed to fetch settings');
      return response.json();
    }
  });
}

export function useUpdateResaleSettings() {
  return useMutation({
    mutationFn: async (settings) => {
      // In development, just return the settings
      if (process.env.NODE_ENV === 'development') {
        return settings;
      }
      const response = await fetch('/api/resale/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (!response.ok) throw new Error('Failed to update settings');
      return response.json();
    }
  });
}

// Resale Orders Hook
export function useResaleOrders({ search, status, dateRange } = {}) {
  return useQuery({
    queryKey: ['resale', 'orders', { search, status, dateRange }],
    queryFn: async () => {
      // In development, return mock data
      if (process.env.NODE_ENV === 'development') {
        return { resaleOrders: mockOrders };
      }

      return fetchGraphQL(GET_RESALE_ORDERS, {
        search,
        status,
        dateRange
      });
    }
  });
}

// Resale Items Hook
export function useResaleItems(filters = {}) {
  return useQuery({
    queryKey: ['resale', 'items', filters],
    queryFn: async () => {
      // In development, return mock data
      if (process.env.NODE_ENV === 'development') {
        return { resaleItems: mockItems };
      }

      return fetchGraphQL(GET_RESALE_ITEMS, { filters });
    }
  });
}

// Single Resale Item Hook
export function useResaleItem(id) {
  return useQuery({
    queryKey: ['resale', 'items', id],
    queryFn: async () => {
      // In development, return mock data
      if (process.env.NODE_ENV === 'development') {
        return { resaleItem: mockItem };
      }

      return fetchGraphQL(GET_RESALE_ITEM, { id });
    },
    enabled: !!id
  });
}

// Resale Analytics Hook
export function useResaleAnalytics() {
  return useQuery({
    queryKey: ['resale', 'analytics'],
    queryFn: async () => {
      // In development, return mock data
      if (process.env.NODE_ENV === 'development') {
        return { resaleAnalytics: mockAnalytics };
      }

      return fetchGraphQL(GET_RESALE_ANALYTICS);
    }
  });
}

// Resale Metrics Hook
export function useResaleMetrics() {
  return useQuery({
    queryKey: ['resale', 'metrics'],
    queryFn: async () => {
      // In development, return mock data
      if (process.env.NODE_ENV === 'development') {
        return { resaleMetrics: mockMetrics };
      }

      return fetchGraphQL(GET_RESALE_METRICS);
    }
  });
}

// Environment Metrics Hook
export function useEnvironmentMetrics({ timeRange } = {}) {
  return useQuery({
    queryKey: ['resale', 'environment', timeRange],
    queryFn: async () => {
      // In development, return mock data
      if (process.env.NODE_ENV === 'development') {
        return { environmentMetrics: mockEnvironmentMetrics };
      }

      return fetchGraphQL(GET_ENVIRONMENT_METRICS, { timeRange });
    }
  });
}

// Add useResaleFinancials hook
export function useResaleFinancials() {
  return useQuery({
    queryKey: ['resale', 'financials'],
    queryFn: async () => {
      // In development, return mock data
      if (process.env.NODE_ENV === 'development') {
        return mockFinancials;
      }
      const response = await fetch('/api/resale/financials');
      if (!response.ok) throw new Error('Failed to fetch financials');
      return response.json();
    }
  });
}

// Update Resale Status Mutation
export function useUpdateResaleStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, status }) => {
      const mutation = `
        mutation UpdateResaleStatus($itemId: ID!, $status: String!) {
          updateResaleStatus(itemId: $itemId, status: $status) {
            id
            status
          }
        }
      `;

      return fetchGraphQL(mutation, { itemId, status });
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries(['resale', 'items']);
      queryClient.invalidateQueries(['resale', 'metrics']);
    }
  });
}

// Update Resale Item Mutation
export function useUpdateResaleItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, ...data }) => {
      const mutation = `
        mutation UpdateResaleItem($itemId: ID!, $data: UpdateResaleItemInput!) {
          updateResaleItem(id: $itemId, data: $data) {
            id
            name
            category
            status
            resalePrice
            specifications
          }
        }
      `;

      return fetchGraphQL(mutation, { itemId, data });
    },
    onSuccess: (data, variables) => {
      // Invalidate and update queries
      queryClient.invalidateQueries(['resale', 'items']);
      queryClient.invalidateQueries(['resale', 'items', variables.itemId]);
    }
  });
}

export function useResaleEnvironment() {
  return useQuery({
    queryKey: ['resale', 'environment'],
    queryFn: async () => {
      // In development, return mock data
      if (process.env.NODE_ENV === 'development') {
        return {
          carbonSavings: [
            { date: '2024-01', value: 100 },
            { date: '2024-02', value: 150 },
            { date: '2024-03', value: 200 }
          ],
          materialRecovery: [
            { material: 'Cotton', amount: 500 },
            { material: 'Polyester', amount: 300 },
            { material: 'Leather', amount: 200 }
          ]
        };
      }
      const response = await fetch('/api/resale/environment');
      if (!response.ok) throw new Error('Failed to fetch environment data');
      return response.json();
    }
  });
}